/**
 * The installer: what a browser tab shows instead of the application.
 *
 * Two steps, each with its real state. The first is the service worker's
 * download of this build, which `sw.ts` reports file by file; when its bar is
 * full the application works with no network. The second is the browser's own
 * installation, behind one button. Chrome fires `beforeinstallprompt` when it
 * judges the page installable, and keeping that event instead of letting
 * Chrome show its banner is what lets the button open the system's install
 * dialog directly, so the person is never told where in a menu to look.
 *
 * Safari has no such event, and Firefox on Android neither, so on those the
 * second step is the one sentence that cannot be avoided: which button of the
 * browser adds the page to the home screen. It is shown only there.
 *
 * Installation is a requirement here and not a comfort; `main.ts` says why.
 */
import { ICON } from "../icons";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface Progress {
  t: "progress";
  done: number;
  total: number;
  bytes: number;
  complete: boolean;
}

// Caught at module load, before `boot` awaits anything: Chrome fires the event
// once, early, and an installer that started listening after it would have no
// button to offer.
let deferred: InstallPromptEvent | null = null;
let installed = false;
const watchers = new Set<() => void>();
const changed = () => watchers.forEach((watcher) => watcher());

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferred = event as InstallPromptEvent;
  changed();
});
window.addEventListener("appinstalled", () => {
  installed = true;
  deferred = null;
  changed();
});

const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("es")} KB`;

/** iPhone and iPad, iPadOS included, which reports itself as a Mac. */
const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);

/** How long to wait for Chrome's event, once the files are in, before
 *  concluding that this browser will not send one. */
const PROMPT_GRACE_MS = 2500;

export function renderInstall(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="installer">
      <div class="uhb">Universidad de La Habana</div>
      <img class="appicon" src="/icon-192.png" alt="">
      <h1>Identidad UH</h1>
      <p class="lede">Tu identidad de la universidad en el teléfono, también sin conexión.</p>
      <ol class="install-steps">
        <li id="step-files">
          <span class="num">1</span>
          <div><b>Descargar la aplicación</b>
            <span class="sub" id="files-status">Preparando la descarga…</span>
            <div class="pbar" id="files-bar"><i></i></div></div>
        </li>
        <li id="step-app">
          <span class="num">2</span>
          <div><b>Instalar en este teléfono</b>
            <span class="sub" id="app-status">Cuando termine la descarga</span>
            <div class="pbar" id="app-bar" hidden><i></i></div></div>
        </li>
      </ol>
      <div class="foot">
        <div id="manual" class="manual" hidden></div>
        <button id="install" disabled>${ICON.download}Instalar</button>
      </div>
    </div>`;

  const $ = <T extends HTMLElement>(selector: string) => root.querySelector<T>(selector)!;
  const filesStep = $("#step-files");
  const filesStatus = $("#files-status");
  const filesFill = $("#files-bar").querySelector("i")!;
  const appStep = $("#step-app");
  const appStatus = $("#app-status");
  const appBar = $("#app-bar");
  const button = $<HTMLButtonElement>("#install");
  const manual = $("#manual");

  let filesReady = false;
  let waiting = false; // accepted in the dialog, the browser still installing
  let gaveUp = false; // the files are in and Chrome sent no event
  let graceTimer: ReturnType<typeof setTimeout> | undefined;
  let alreadyThere = false;

  const draw = () => {
    if (installed) {
      appStep.classList.add("done");
      appBar.hidden = true;
      appStatus.textContent = "Instalada. Ábrela desde la pantalla de inicio: «Identidad UH».";
      button.hidden = true;
      manual.hidden = true;
      return;
    }
    if (waiting) {
      appBar.hidden = false;
      appBar.classList.add("ind");
      appStatus.textContent = "El teléfono la está instalando…";
      button.hidden = true;
      return;
    }
    if (deferred) {
      button.hidden = false;
      button.disabled = !filesReady;
      button.innerHTML = filesReady ? `${ICON.download}Instalar` : "Descargando…";
      appStatus.textContent = filesReady ? "Un toque y queda en tu pantalla de inicio" : "Cuando termine la descarga";
      manual.hidden = true;
      return;
    }
    if (alreadyThere) {
      appStep.classList.add("done");
      appStatus.textContent = "Ya está instalada. Ábrela desde la pantalla de inicio: «Identidad UH».";
      button.hidden = true;
      return;
    }
    if (!gaveUp) {
      button.disabled = true;
      button.innerHTML = filesReady ? "Preparando…" : "Descargando…";
      return;
    }
    // No event from this browser. Safari and Firefox never send one, and
    // neither does Chrome for a page it already installed or whose dialog was
    // turned down: the one place left to say where to tap.
    button.hidden = true;
    manual.hidden = false;
    appStatus.textContent = "Desde el menú del navegador";
    manual.innerHTML = isIOS()
      ? `<p>Toca ${ICON.share}<b>Compartir</b> abajo y luego
         ${ICON.homeScreen}<b>Agregar a inicio</b>.</p>`
      : `<p>Abre el menú ${ICON.more} del navegador y toca
         <b>Instalar aplicación</b> o <b>Agregar a la pantalla de inicio</b>.</p>`;
  };

  const onFiles = (progress: Progress) => {
    if (progress.complete) {
      filesReady = true;
      filesStep.classList.add("done");
      filesFill.style.width = "100%";
      filesStatus.textContent = progress.bytes > 0
        ? `Lista para usar sin conexión · ${kb(progress.bytes)}`
        : "Lista para usar sin conexión";
      clearTimeout(graceTimer);
      graceTimer = setTimeout(() => {
        gaveUp = true;
        draw();
      }, PROMPT_GRACE_MS);
    } else if (progress.total > 0) {
      filesFill.style.width = `${Math.round((100 * progress.done) / progress.total)}%`;
      filesStatus.textContent =
        `${progress.done} de ${progress.total} archivos · ${kb(progress.bytes)}`;
    }
    draw();
  };

  const onMessage = (event: MessageEvent) => {
    if ((event.data as Progress | null)?.t === "progress") onFiles(event.data as Progress);
  };

  // No service worker (an old browser, plain HTTP, the dev server): nothing to
  // download, and nothing that would work offline either. Say so and let the
  // install go ahead; the browser decides whether it can.
  const noWorker = () => {
    filesStatus.textContent = "Este navegador no guarda la aplicación para usarla sin conexión";
    onFiles({ t: "progress", done: 0, total: 0, bytes: 0, complete: true });
  };

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onMessage);
    navigator.serviceWorker.startMessages();
    void navigator.serviceWorker.getRegistration().then((registration) => {
      const worker = registration?.installing ?? registration?.waiting ?? registration?.active;
      if (worker) worker.postMessage({ t: "status" });
      else noWorker();
    }, noWorker);
  } else {
    noWorker();
  }

  // Chrome on Android answers whether this same application is already
  // installed, through `related_applications` in the manifest; a person who
  // installed it and then opened the link in the browser is told to open the
  // icon rather than shown an install that cannot happen.
  const related = (navigator as { getInstalledRelatedApps?: () => Promise<unknown[]> })
    .getInstalledRelatedApps;
  if (related) {
    void related.call(navigator).then((apps) => {
      alreadyThere = apps.length > 0;
      draw();
    }, () => {});
  }

  button.addEventListener("click", async () => {
    const event = deferred;
    if (!event) return;
    // Asked here, under the tap: Chrome grants persistent storage to an
    // installed application, and asking is what makes the grant stick
    // before the first enrolment writes the device key.
    void navigator.storage?.persist?.().catch(() => false);
    await event.prompt();
    const { outcome } = await event.userChoice;
    // An event prompts once; Chrome fires a new one if it will offer again.
    deferred = null;
    if (outcome === "accepted") waiting = true;
    else gaveUp = true;
    draw();
  });

  watchers.add(draw);
  draw();
  return () => {
    watchers.delete(draw);
    clearTimeout(graceTimer);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    }
  };
}
